import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, SegmentedControl } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * Simple text input field with label.
 */
export function FieldRow({ label, placeholder, value, onChange, maxLength, hint }: {
  label: string; placeholder: string; value: string; onChange: (v: string) => void; maxLength?: number; hint?: string;
}) {
  return (
    <div className="flex flex-col gap-[2px]">
      <Text as="label" className="text-[length:var(--text-meta)] font-[var(--font-medium)] text-[var(--text-tertiary)]">
        {label}
      </Text>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn(
          'rounded-(--radius-md) border border-[var(--border-subtle)]',
          'px-[var(--space-3)] py-[var(--space-2)] h-9',
          'text-[length:var(--text-body-sm)] text-[var(--text-primary)]',
          'bg-[var(--surface-primary)] placeholder:text-[var(--text-tertiary)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--seed-base)] focus:border-transparent',
        )}
      />
      {hint && (
        <Text as="span" className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
          {hint}
        </Text>
      )}
    </div>
  );
}

/**
 * Combo input: shows selectable chip suggestions from previous entries + text input.
 * First time (no suggestions) renders a plain text input. After the first tower is
 * added, subsequent units can click a chip to reuse it.
 */
export function ComboField({ label, placeholder, value, suggestions, onChange }: {
  label: string; placeholder: string; value: string; suggestions: string[]; onChange: (v: string) => void;
}) {
  const { t } = useTranslation('features/launches');
  const items = useMemo(
    () => suggestions.map((s) => ({ value: s, label: s })),
    [suggestions],
  );

  return (
    <div className="flex flex-col gap-[2px]">
      <Text as="label" className="text-[length:var(--text-meta)] font-[var(--font-medium)] text-[var(--text-tertiary)]">
        {label}
      </Text>
      {suggestions.length > 0 ? (
        <div className="flex flex-col gap-[var(--space-1)]">
          <SegmentedControl
            value={value}
            onChange={onChange}
            items={items}
            variant="pill"
            size="sm"
            ariaLabel={label}
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('create.comboOtherPlaceholder')}
            className={cn(
              'rounded-(--radius-md) border border-[var(--border-subtle)]',
              'px-[var(--space-3)] py-[var(--space-2)] h-9',
              'text-[length:var(--text-body-sm)] text-[var(--text-primary)]',
              'bg-[var(--surface-primary)] placeholder:text-[var(--text-tertiary)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--seed-base)] focus:border-transparent',
            )}
          />
        </div>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'rounded-(--radius-md) border border-[var(--border-subtle)]',
            'px-[var(--space-3)] py-[var(--space-2)] h-9',
            'text-[length:var(--text-body-sm)] text-[var(--text-primary)]',
            'bg-[var(--surface-primary)] placeholder:text-[var(--text-tertiary)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--seed-base)] focus:border-transparent',
          )}
        />
      )}
    </div>
  );
}

/**
 * Native select dropdown for predefined options + any custom values used previously.
 */
export function SelectField({ label, placeholder, value, options, extraOptions, onChange }: {
  label: string; placeholder: string; value: string;
  options: string[]; extraOptions: string[]; onChange: (v: string) => void;
}) {
  const allOptions = useMemo(() => {
    const extras = extraOptions.filter((e) => !options.includes(e));
    return [...options, ...extras];
  }, [options, extraOptions]);

  return (
    <div className="flex flex-col gap-[2px]">
      <Text as="label" className="text-[length:var(--text-meta)] font-[var(--font-medium)] text-[var(--text-tertiary)]">
        {label}
      </Text>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'rounded-(--radius-md) border border-[var(--border-subtle)]',
          'px-[var(--space-3)] py-[var(--space-2)] h-9',
          'text-[length:var(--text-body-sm)] text-[var(--text-primary)]',
          'bg-[var(--surface-primary)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--seed-base)] focus:border-transparent',
          'appearance-none cursor-pointer',
          !value && 'text-[var(--text-tertiary)]',
        )}
      >
        <option value="">{placeholder}</option>
        {allOptions.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}
